import React, { useEffect, useState } from "react";
import { httpsCallable } from "firebase/functions";
import { collection, query, orderBy, limit, onSnapshot } from "firebase/firestore";
import { signOut } from "firebase/auth";
import { auth, db, functions } from "../utils/firebase";
import "./Admin.css";

const RANK_TYPES = [
  { value: "AD", label: "Active Duty (AD)" },
  { value: "AR", label: "Active Reserve (AR)" },
  { value: "DR", label: "Drill/Reserve (DR)" },
];

const RANK_LEVELS = [
  { value: "C", label: "Corporal track (C)" },
  { value: "S", label: "Sergeant track (S)" },
];

// Defaults to NEXT month, not the current one - cutting scores for a given
// month post in the back half of the PRECEDING month (this mirrors
// get_current_score_month() in the real scraper). Defaulting to the
// current month here was the same off-by-one-month bug class as the one
// that caused this whole session's incident, just on the admin form
// instead of the scraper - an admin manually backfilling a fresh release
// would have had the wrong month prefilled.
const getDefaultYYYYMM = () => {
  const now = new Date();
  const next = new Date(now.getFullYear(), now.getMonth() + 1, 1);
  const year = next.getFullYear();
  const month = String(next.getMonth() + 1).padStart(2, "0");
  return `${year}${month}`;
};

// Parses the textarea contents into a { mosCode: score } object.
// Blank lines are skipped; every non-blank line must match "MOScode,score"
// where score is either digits or the literal string "Closed". MOS codes
// are digits-only, 1-4 characters, auto-padded to 4 - matching the
// scraper's own normalize_mos_code() exactly. The old pattern accepted
// letters, which normalize_mos_code() never produces, so manual entry
// could create MOS keys the app's own data never contains.
const parseScoresInput = (text) => {
  const lines = text.split("\n");
  const errors = [];
  const scores = {};

  lines.forEach((rawLine, idx) => {
    const line = rawLine.trim();
    if (line === "") return;

    const match = line.match(/^(\d{1,4}),(\d+|Closed)$/);
    if (!match) {
      errors.push(
        `Line ${idx + 1}: "${rawLine}" — expected format "MOScode,score" (e.g. "0111,581" or "0111,Closed"), MOS code must be 1-4 digits`
      );
      return;
    }

    const [, rawMosCode, score] = match;
    const mosCode = rawMosCode.padStart(4, "0");
    scores[mosCode] = score;
  });

  return { scores, errors };
};

const formatDuration = (durationMs) => {
  if (typeof durationMs !== "number" || Number.isNaN(durationMs)) return "-";
  if (durationMs >= 1000) return `${(durationMs / 1000).toFixed(1)}s`;
  return `${durationMs}ms`;
};

const formatStartedAt = (startedAt) => {
  if (!startedAt) return "-";
  const date = typeof startedAt.toDate === "function" ? startedAt.toDate() : new Date(startedAt);
  return date.toLocaleString();
};

const ManualScoreEntry = () => {
  const [rankType, setRankType] = useState("AD");
  const [rankLevel, setRankLevel] = useState("C");
  const [monthId, setMonthId] = useState(getDefaultYYYYMM());
  const [scoresText, setScoresText] = useState("");
  const [validationErrors, setValidationErrors] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { type: 'success'|'error', message: string }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setResult(null);
    setValidationErrors([]);

    const errors = [];

    if (!/^\d{4}(0[1-9]|1[0-2])$/.test(monthId)) {
      errors.push("Month must be in YYYYMM format (e.g. 202608), with a valid month 01-12.");
    }

    if (scoresText.trim() === "") {
      errors.push("Enter at least one MOS score line.");
    }

    const { scores, errors: parseErrors } = parseScoresInput(scoresText);
    errors.push(...parseErrors);

    if (errors.length > 0) {
      setValidationErrors(errors);
      return;
    }

    setSubmitting(true);
    try {
      const submitManualScore = httpsCallable(functions, "submit_manual_score");
      const response = await submitManualScore({
        rankType,
        rankLevel,
        monthId,
        scores,
      });

      const written = response?.data?.written ?? Object.keys(scores).length;
      setResult({
        type: "success",
        message: `Saved ${written} MOS scores to ${rankType} / ${rankLevel} / ${monthId}`,
      });
    } catch (err) {
      console.error("submit_manual_score failed:", err);
      setResult({
        type: "error",
        message: err?.message || "Failed to submit scores. Please try again.",
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="admin-panel">
      <h2>Manual Score Entry</h2>

      {result && (
        <div className={`admin-alert ${result.type === "success" ? "admin-alert-success" : "admin-alert-error"}`}>
          {result.message}
        </div>
      )}

      {validationErrors.length > 0 && (
        <div className="admin-alert admin-alert-error">
          <strong>Please fix the following before submitting:</strong>
          <ul className="admin-error-list">
            {validationErrors.map((msg, i) => (
              <li key={i}>{msg}</li>
            ))}
          </ul>
        </div>
      )}

      <form onSubmit={handleSubmit} noValidate>
        <div className="admin-form-row">
          <div className="admin-field">
            <label htmlFor="rank-type">Rank Type</label>
            <select
              id="rank-type"
              value={rankType}
              onChange={(e) => setRankType(e.target.value)}
              disabled={submitting}
            >
              {RANK_TYPES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-field">
            <label htmlFor="rank-level">Rank Level</label>
            <select
              id="rank-level"
              value={rankLevel}
              onChange={(e) => setRankLevel(e.target.value)}
              disabled={submitting}
            >
              {RANK_LEVELS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>

          <div className="admin-field">
            <label htmlFor="month-id">Month (YYYYMM)</label>
            <input
              id="month-id"
              type="text"
              value={monthId}
              placeholder="202608"
              onChange={(e) => setMonthId(e.target.value)}
              disabled={submitting}
            />
          </div>
        </div>

        <div className="admin-field">
          <label htmlFor="scores-text">MOS Scores</label>
          <div className="admin-format-hint">
            One MOS per line, format <code>MOScode,score</code> — e.g. <code>0111,581</code> or{" "}
            <code>0111,Closed</code>. Score must be digits only, or exactly the word{" "}
            <code>Closed</code>.
          </div>
          <textarea
            id="scores-text"
            className="admin-textarea"
            value={scoresText}
            onChange={(e) => setScoresText(e.target.value)}
            disabled={submitting}
            placeholder={"0111,581\n0121,Closed\n0151,594"}
          />
        </div>

        <button type="submit" className="admin-btn" disabled={submitting}>
          {submitting ? "Submitting..." : "Submit Scores"}
        </button>
      </form>
    </section>
  );
};

const FunctionRunsPanel = () => {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const runsQuery = query(collection(db, "functionRuns"), orderBy("startedAt", "desc"), limit(50));

    const unsubscribe = onSnapshot(
      runsQuery,
      (snapshot) => {
        setRuns(snapshot.docs.map((doc) => ({ id: doc.id, ...doc.data() })));
        setLoading(false);
      },
      (err) => {
        console.error("Failed to subscribe to functionRuns:", err);
        setError("Failed to load function runs.");
        setLoading(false);
      }
    );

    return () => unsubscribe();
  }, []);

  return (
    <section className="admin-panel">
      <h2>Recent Function Runs</h2>

      {error && <div className="admin-alert admin-alert-error">{error}</div>}

      {loading ? (
        <div className="admin-empty-state">Loading...</div>
      ) : runs.length === 0 ? (
        <div className="admin-empty-state">No function runs yet.</div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table">
            <thead>
              <tr>
                <th>Function</th>
                <th>Status</th>
                <th>Started</th>
                <th>Duration</th>
                <th>Message/Error</th>
              </tr>
            </thead>
            <tbody>
              {runs.map((run) => (
                <tr key={run.id}>
                  <td>{run.functionName}</td>
                  <td>
                    <span className={run.status === "success" ? "admin-status-success" : "admin-status-failure"}>
                      {run.status}
                    </span>
                  </td>
                  <td>{formatStartedAt(run.startedAt)}</td>
                  <td>{formatDuration(run.durationMs)}</td>
                  <td>{run.status === "failure" && run.error ? run.error : run.message || "-"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
};

const UserStatsPanel = () => {
  const [stats, setStats] = useState(null); // { totalUsers, activeUsers30d }
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    const getUserStats = httpsCallable(functions, "get_user_stats");
    getUserStats()
      .then((response) => setStats(response.data))
      .catch((err) => {
        console.error("get_user_stats failed:", err);
        setError(err?.message || "Failed to load user stats.");
      })
      .finally(() => setLoading(false));
  }, []);

  return (
    <section className="admin-panel">
      <h2>Users</h2>
      {error && <div className="admin-alert admin-alert-error">{error}</div>}
      {loading ? (
        <div className="admin-empty-state">Loading...</div>
      ) : stats ? (
        <div className="admin-stat-row">
          <div className="admin-stat">
            <div className="admin-stat-value">{stats.activeUsers30d}</div>
            <div className="admin-stat-label">Active in last 30 days</div>
          </div>
          <div className="admin-stat">
            <div className="admin-stat-value">{stats.totalUsers}</div>
            <div className="admin-stat-label">Total users</div>
          </div>
        </div>
      ) : null}
    </section>
  );
};

const AdminDashboard = () => {
  const handleSignOut = () => {
    signOut(auth).catch((err) => console.error("Sign out failed:", err));
  };

  return (
    <div className="admin-dashboard-page">
      <div className="admin-header">
        <h1>DevilRank Admin</h1>
        <div>
          {auth.currentUser && <span className="admin-user-email">{auth.currentUser.email}</span>}{" "}
          <button className="admin-signout-link" onClick={handleSignOut}>
            Sign out
          </button>
        </div>
      </div>

      <UserStatsPanel />
      <ManualScoreEntry />
      <FunctionRunsPanel />
    </div>
  );
};

export default AdminDashboard;
